import { useId, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Chip, Stack, Typography } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import PendingRequestDetails from './PendingRequestDetails';

function RequestCategory({ requests, idPrefix, onCancelRequest, cancelling }) {
  const [expandedRequest, setExpandedRequest] = useState(null);
  return (
    <Stack spacing={1}>
      {requests.map((request, index) => {
        const key = `${request.sheetName}-${request.sheetRowNumber}-${index}`;
        const person = [request.usuarioNombre, request.usuarioApellido].filter(Boolean).join(' ');
        const location = [request.pais, request.iglesiaEstado, request.iglesiaCiudad, request.iglesiaNombre].filter(Boolean).join(' / ');
        const id = `${idPrefix}-request-${index}`;
        return (
          <Accordion key={key} expanded={expandedRequest === key} onChange={(_, expanded) => setExpandedRequest(expanded ? key : null)}
            disableGutters elevation={0} slotProps={{ transition: { unmountOnExit: true } }}
            sx={{ minWidth: 0, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ChevronDown size={18} />} id={`${id}-header`} aria-controls={`${id}-content`}>
              <Stack spacing={0.25} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{person || request.usuarioEmail || 'Usuario no registrado'}</Typography>
                {person && request.usuarioEmail && <Typography variant="caption" color="text.secondary">{request.usuarioEmail}</Typography>}
                {location && <Typography variant="caption" color="text.secondary">{location}</Typography>}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
              <PendingRequestDetails request={request} showUser={false} onCancelRequest={onCancelRequest} cancelling={cancelling} />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}

export default function PendingRequestsOverview({ requests = [], title = 'Resumen de cambios pendientes', onCancelRequest, cancelling = false }) {
  const [expandedType, setExpandedType] = useState(null);
  const overviewId = useId();
  const groups = useMemo(() => {
    const byType = new Map();
    requests.forEach((request) => {
      const type = request.type || 'Otras solicitudes';
      if (!byType.has(type)) byType.set(type, []);
      byType.get(type).push(request);
    });
    return [...byType.entries()];
  }, [requests]);

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {title && <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>}
      {groups.length === 0 && <Alert severity="info">No hay solicitudes pendientes.</Alert>}
      {groups.map(([type, categoryRequests], index) => {
        const id = `${overviewId}-category-${index}`;
        return (
          <Accordion key={type} expanded={expandedType === type} onChange={(_, expanded) => setExpandedType(expanded ? type : null)}
            disableGutters elevation={0} slotProps={{ transition: { unmountOnExit: true } }}
            sx={{ minWidth: 0, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ChevronDown size={18} />} id={`${id}-header`} aria-controls={`${id}-content`}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2">{type}</Typography>
                <Chip label={categoryRequests.length} size="small" color="primary"
                  aria-label={`${categoryRequests.length} ${categoryRequests.length === 1 ? 'solicitud' : 'solicitudes'}`} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
              {categoryRequests.length === 1
                ? <PendingRequestDetails request={categoryRequests[0]} onCancelRequest={onCancelRequest} cancelling={cancelling} />
                : <RequestCategory requests={categoryRequests} idPrefix={id} onCancelRequest={onCancelRequest} cancelling={cancelling} />}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}
